import React, { useEffect, useMemo, useState } from 'react';
import { api, getToken } from '../api.js';
import Modal from '../components/Modal.jsx';
import { fmtIST } from '../lib/datetime.js';

const FIELD_TYPES = [
  { value: 'text',     label: 'Text' },
  { value: 'tel',      label: 'Phone' },
  { value: 'email',    label: 'Email' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select',   label: 'Dropdown' },
  { value: 'number',   label: 'Number' },
];

const KNOWN_PREFILLS = ['name','phone','email','designation','city','state','org_name','country'];

export default function Forms() {
  const [view, setView] = useState('list'); // list | detail
  const [forms, setForms] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);

  async function load() {
    try { const r = await api.get('/api/forms'); setForms(r.rows || []); } catch {}
  }
  useEffect(() => { load(); }, []);

  function openDetail(id) { setActiveId(id); setView('detail'); }
  function backToList()   { setActiveId(null); setView('list'); load(); }

  return (
    <div className="px-8 py-6 max-w-[1300px] mx-auto">
      {view === 'list' && (
        <FormsList forms={forms} onOpen={openDetail} onCreate={() => setOpenCreate(true)} />
      )}
      {view === 'detail' && activeId && (
        <FormDetail id={activeId} onBack={backToList} />
      )}

      <Modal open={openCreate} onClose={() => setOpenCreate(false)} title="New form" maxWidth="max-w-3xl">
        <FormBuilder
          initial={{ name: '', description: '', fields: [], consent_label: '', thank_you_text: '' }}
          onSave={async (payload) => {
            try { await api.post('/api/forms', payload); setOpenCreate(false); load(); }
            catch (e) { alert(e.body?.error || e.message); }
          }}
          onCancel={() => setOpenCreate(false)}
        />
      </Modal>
    </div>
  );
}

/* ============================================================================
 * LIST
 * ============================================================================ */
function FormsList({ forms, onOpen, onCreate }) {
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Forms</h1>
          <p className="text-sm text-slate-500 mt-0.5">Reusable forms power campaign data collection. Submissions land in each form's data bank.</p>
        </div>
        <button className="btn" onClick={onCreate}>+ New form</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {forms.map(f => (
          <button key={f.id} className="card p-5 text-left hover:border-slate-300 transition" onClick={() => onOpen(f.id)}>
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="text-base font-semibold text-slate-900 truncate flex-1">{f.name}</h3>
              {f.is_system ? <span className="text-[10px] uppercase tracking-wider text-slate-400">system</span> : null}
            </div>
            <p className="text-xs text-slate-500 truncate mb-3">{f.description || 'No description'}</p>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <span><strong className="text-slate-900">{f.field_count}</strong> fields</span>
              <span className="text-slate-300">·</span>
              <span><strong className="text-slate-900">{Number(f.submission_count).toLocaleString()}</strong> submissions</span>
            </div>
          </button>
        ))}
        {!forms.length && (
          <div className="md:col-span-2 card p-12 text-center">
            <div className="text-slate-400 text-sm">No forms yet.</div>
            <button className="btn mt-3" onClick={onCreate}>Create your first form</button>
          </div>
        )}
      </div>
    </>
  );
}

/* ============================================================================
 * DETAIL — tabs: Builder · Data bank · Settings
 * ============================================================================ */
function FormDetail({ id, onBack }) {
  const [tab, setTab] = useState('builder');
  const [form, setForm] = useState(null);

  async function load() {
    try { const r = await api.get(`/api/forms/${id}`); setForm(r.form); } catch {}
  }
  useEffect(() => { load(); }, [id]);

  async function save(payload) {
    try { await api.patch(`/api/forms/${id}`, payload); load(); }
    catch (e) { alert(e.body?.error || e.message); }
  }
  async function remove() {
    if (!form || form.is_system) return;
    if (!window.confirm(`Delete "${form.name}"? Submissions must be empty.`)) return;
    try { await api.del(`/api/forms/${id}`); onBack(); }
    catch (e) { alert(e.body?.error || e.message); }
  }

  if (!form) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex-1 min-w-0">
          <button className="text-sm text-slate-500 hover:text-slate-700 mb-1.5" onClick={onBack}>← All forms</button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900 truncate">{form.name}</h1>
            {form.is_system ? <span className="text-[10px] uppercase tracking-wider text-slate-400">system</span> : null}
          </div>
          {form.description && <p className="text-sm text-slate-500 mt-1">{form.description}</p>}
        </div>
        {!form.is_system && <button className="btn-ghost text-rose-600 border-rose-100 hover:bg-rose-50" onClick={remove}>Delete</button>}
      </div>

      <div className="flex border-b border-slate-200 mb-5">
        <TabBtn active={tab === 'builder'}  onClick={() => setTab('builder')}>Builder</TabBtn>
        <TabBtn active={tab === 'preview'}  onClick={() => setTab('preview')}>Preview</TabBtn>
        <TabBtn active={tab === 'data'}     onClick={() => setTab('data')}>Data bank</TabBtn>
        <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')}>Settings</TabBtn>
      </div>

      {tab === 'builder'  && <FormBuilder initial={form} onSave={save} embedded />}
      {tab === 'preview'  && <FormPreview form={form} />}
      {tab === 'data'     && <DataBank formId={id} />}
      {tab === 'settings' && <FormSettings form={form} onSave={save} />}
    </>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px
      ${active ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
      {children}
    </button>
  );
}

/* ============================================================================
 * BUILDER — field schema editor
 * ============================================================================ */
function FormBuilder({ initial, onSave, onCancel, embedded }) {
  const [name, setName]               = useState(initial.name || '');
  const [description, setDescription] = useState(initial.description || '');
  const [fields, setFields]           = useState(initial.fields || []);
  const [consentLabel, setConsentLabel] = useState(initial.consent_label || '');
  const [thankYou, setThankYou]       = useState(initial.thank_you_text || '');
  const [busy, setBusy] = useState(false);

  function addField() {
    setFields(arr => [...arr, { key: `field_${arr.length + 1}`, label: 'New field', type: 'text', required: false }]);
  }
  function updateField(i, patch) {
    setFields(arr => arr.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  }
  function removeField(i) {
    setFields(arr => arr.filter((_, idx) => idx !== i));
  }
  function move(i, delta) {
    const j = i + delta;
    if (j < 0 || j >= fields.length) return;
    const out = [...fields];
    [out[i], out[j]] = [out[j], out[i]];
    setFields(out);
  }

  async function save() {
    setBusy(true);
    try {
      await onSave({ name, description, fields, consent_label: consentLabel, thank_you_text: thankYou });
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      {!embedded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="label-l">Form name</div>
            <input className="input w-full" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Booth Booking Inquiry" />
          </div>
          <div>
            <div className="label-l">Description (admin-only)</div>
            <input className="input w-full" value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief context" />
          </div>
        </div>
      )}

      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-900">Fields</h4>
          <button className="btn-ghost btn-sm" onClick={addField}>+ Add field</button>
        </div>
        {!fields.length && <div className="text-sm text-slate-400 py-6 text-center">No fields yet.</div>}
        <div className="space-y-2">
          {fields.map((f, i) => (
            <FieldRow key={i} f={f} idx={i} total={fields.length}
              onUpdate={patch => updateField(i, patch)}
              onRemove={() => removeField(i)}
              onMove={delta => move(i, delta)} />
          ))}
        </div>
      </div>

      <div className="card p-5 space-y-3">
        <h4 className="text-sm font-semibold text-slate-900">Wording</h4>
        <div>
          <div className="label-l">Consent checkbox label (optional)</div>
          <input className="input w-full" value={consentLabel} onChange={e => setConsentLabel(e.target.value)} placeholder="I agree to receive updates by WhatsApp and email." />
        </div>
        <div>
          <div className="label-l">Thank-you message (after submit)</div>
          <textarea className="input w-full h-20" value={thankYou} onChange={e => setThankYou(e.target.value)} placeholder="Thanks — we'll be in touch shortly." />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && <button className="btn-ghost" onClick={onCancel}>Cancel</button>}
        <button className="btn" onClick={save} disabled={busy || !name.trim() || fields.length === 0}>
          {busy ? 'Saving…' : embedded ? 'Save changes' : 'Create form'}
        </button>
      </div>
    </div>
  );
}

function FieldRow({ f, idx, total, onUpdate, onRemove, onMove }) {
  const isSelect = f.type === 'select';
  return (
    <div className="rounded-lg border border-slate-200 p-3 hover:border-slate-300 transition">
      <div className="grid grid-cols-12 gap-2 items-start">
        <div className="col-span-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Key (no spaces)</div>
          <input className="input w-full font-mono text-xs"
                 value={f.key}
                 onChange={e => onUpdate({ key: e.target.value.replace(/[^a-z0-9_]/gi, '').slice(0, 32) })} />
        </div>
        <div className="col-span-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Label (visible)</div>
          <input className="input w-full" value={f.label} onChange={e => onUpdate({ label: e.target.value })} />
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Type</div>
          <select className="input w-full" value={f.type} onChange={e => onUpdate({ type: e.target.value })}>
            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Prefill from</div>
          <select className="input w-full" value={f.prefill || ''} onChange={e => onUpdate({ prefill: e.target.value || undefined })}>
            <option value="">—</option>
            {KNOWN_PREFILLS.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div className="col-span-1 flex flex-col items-end gap-1">
          <button className="text-slate-400 hover:text-slate-700 text-xs" onClick={() => onMove(-1)} disabled={idx === 0}>↑</button>
          <button className="text-slate-400 hover:text-slate-700 text-xs" onClick={() => onMove(1)} disabled={idx === total - 1}>↓</button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs">
        <label className="inline-flex items-center gap-1.5 text-slate-600">
          <input type="checkbox" checked={!!f.required} onChange={e => onUpdate({ required: e.target.checked })} /> Required
        </label>
        <div className="flex-1" />
        <button className="text-rose-600 hover:text-rose-700 text-xs" onClick={onRemove}>Remove</button>
      </div>
      {isSelect && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Options (one per line)</div>
          <textarea
            className="input w-full h-20 font-mono text-xs"
            value={(f.options || []).join('\n')}
            onChange={e => onUpdate({ options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
          />
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * DATA BANK
 * ============================================================================ */
function DataBank({ formId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const limit = 50;

  async function load(off = 0) {
    setBusy(true);
    try {
      const [r, f] = await Promise.all([
        api.get(`/api/forms/${formId}/submissions?limit=${limit}&offset=${off}`),
        form ? Promise.resolve({ form }) : api.get(`/api/forms/${formId}`),
      ]);
      setRows(r.rows);
      setTotal(r.total);
      setOffset(off);
      if (!form) setForm(f.form);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(0); /* eslint-disable-line */ }, [formId]);

  function downloadCsv() {
    const tok = getToken();
    const url = `/api/forms/${formId}/submissions/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `${(form && form.slug) || 'form'}-submissions.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  async function remove(subId) {
    if (!window.confirm('Delete this submission? The recipient will be able to re-submit using the same link.')) return;
    try {
      await api.del(`/api/forms/${formId}/submissions/${subId}`);
      load(offset);
    } catch (e) { alert(e.body?.error || e.message); }
  }

  if (!form) return <div className="text-sm text-slate-400">Loading…</div>;
  const fields = form.fields || [];

  return (
    <>
      <div className="card p-4 mb-4 flex items-center gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{total.toLocaleString()} submissions</h3>
          <p className="text-xs text-slate-500 mt-0.5">Data accumulates here from every campaign that uses this form. Deleting a row also unlocks the recipient's link for re-submission.</p>
        </div>
        <div className="flex-1" />
        <button className="btn-ghost" onClick={() => load(offset)} disabled={busy}>Refresh</button>
        <button className="btn" onClick={downloadCsv}>Download CSV</button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5 text-left">Submitted</th>
                <th className="px-3 py-2.5 text-left">Campaign</th>
                <th className="px-3 py-2.5 text-left">Source</th>
                {fields.map(f => <th key={f.key} className="px-3 py-2.5 text-left">{f.label}</th>)}
                <th className="px-3 py-2.5 text-left">Consent</th>
                <th className="px-3 py-2.5 text-right pr-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap tabular-nums">{fmtIST(r.submitted_at)}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 truncate max-w-[200px]">{r.campaign_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2"><SourcePill source={r.source_channel} /></td>
                  {fields.map(f => (
                    <td key={f.key} className="px-3 py-2 text-slate-700 truncate max-w-[200px]" title={r.data?.[f.key] || ''}>
                      {r.data?.[f.key] || <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-xs">{r.consent ? <span className="text-emerald-600">yes</span> : <span className="text-slate-400">no</span>}</td>
                  <td className="px-3 py-2 pr-4 text-right">
                    <button className="text-rose-600 hover:text-rose-700 text-xs font-medium" onClick={() => remove(r.id)} title="Delete + reset link">Delete</button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={fields.length + 5} className="px-3 py-12 text-center text-slate-400">No submissions yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}</span>
          <div className="flex gap-2">
            <button className="btn-ghost btn-sm" disabled={offset === 0 || busy} onClick={() => load(Math.max(0, offset - limit))}>← Prev</button>
            <button className="btn-ghost btn-sm" disabled={offset + limit >= total || busy} onClick={() => load(offset + limit)}>Next →</button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================================================================
 * PREVIEW — renders the form exactly as a recipient would see it.
 * Submit is intercepted with a "preview only" toast. No DB writes.
 * ============================================================================ */
function FormPreview({ form }) {
  const [values, setValues] = useState(() => {
    const init = {};
    for (const f of form.fields || []) init[f.key] = f.prefill ? `(${f.prefill})` : '';
    return init;
  });
  const [consent, setConsent] = useState(true);
  const [showSubmitted, setShowSubmitted] = useState(false);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="card p-5 mb-3 bg-amber-50/40 border-amber-200">
          <h4 className="text-sm font-semibold text-amber-800">Preview mode</h4>
          <p className="text-xs text-amber-700 mt-0.5">This is how the form looks to a recipient. Pre-fill columns show <code>(source)</code> placeholders — at send time those are replaced with the contact's real values. Submit doesn't write to the data bank.</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-6">
          <header className="text-center mb-6">
            <img src="/iaapi-logo.png" alt="IAAPI" className="h-10 w-auto mx-auto" />
            <div className="mt-1.5 text-[11px] text-slate-500">Indian Association of Amusement Parks &amp; Industries</div>
          </header>

          {showSubmitted ? (
            <div className="card p-7 text-center">
              <h2 className="text-lg font-semibold text-brand-700">Thank you!</h2>
              <p className="mt-2 text-sm text-slate-600">{form.thank_you_text || 'Your details are in.'}</p>
              <button className="btn-ghost btn-sm mt-4" onClick={() => setShowSubmitted(false)}>← Back to form</button>
            </div>
          ) : (
            <form onSubmit={e => { e.preventDefault(); setShowSubmitted(true); }} className="card p-6">
              <h2 className="text-lg font-semibold text-brand-700">{form.name}</h2>
              <p className="text-sm text-slate-600 mt-1.5 mb-5">Confirm or edit your details below.</p>
              {(form.fields || []).map(f => (
                <PreviewFieldRow key={f.key} field={f} value={values[f.key] || ''} onChange={v => setValues(s => ({ ...s, [f.key]: v }))} />
              ))}
              {form.consent_label && (
                <label className="flex items-start gap-2.5 mt-3 mb-1 text-sm text-slate-600">
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-1" />
                  <span>{form.consent_label}</span>
                </label>
              )}
              <button className="btn w-full mt-4 py-3 text-base">Submit (preview)</button>
            </form>
          )}
        </div>
      </div>

      <aside className="lg:sticky lg:top-6 self-start space-y-3">
        <div className="card p-5">
          <h4 className="text-sm font-semibold text-slate-900 mb-2">Schema summary</h4>
          <div className="text-xs text-slate-600 space-y-1.5">
            <div><strong className="text-slate-900">{form.fields?.length || 0}</strong> fields</div>
            <div>{(form.fields || []).filter(f => f.required).length} required</div>
            <div>{(form.fields || []).filter(f => f.prefill).length} with prefill</div>
          </div>
        </div>
        <div className="card p-5">
          <h4 className="text-sm font-semibold text-slate-900 mb-2">Live URL</h4>
          <p className="text-xs text-slate-500 mb-2">When a campaign uses this form, recipients open:</p>
          <code className="block text-[11px] bg-slate-50 px-2 py-1.5 rounded font-mono break-all">https://campaigns.iaapi.org/r/&lt;token&gt;</code>
          <p className="text-[11px] text-slate-500 mt-2">Each (campaign, contact) pair gets a unique token.</p>
        </div>
      </aside>
    </div>
  );
}

function PreviewFieldRow({ field, value, onChange }) {
  const required = !!field.required;
  return (
    <div className="mb-4">
      <div className="label-l">{field.label}{required && <span className="text-rose-500"> *</span>}</div>
      {field.type === 'select' ? (
        <select className="input w-full" value={value} onChange={e => onChange(e.target.value)} required={required}>
          <option value="">Select…</option>
          {(field.options || []).map(opt => <option key={opt}>{opt}</option>)}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea className="input w-full h-24" value={value} onChange={e => onChange(e.target.value)} required={required} />
      ) : field.type === 'tel' ? (
        <div className="flex">
          <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-500">+91</span>
          <input className="input rounded-l-none w-full" value={value} onChange={e => onChange(e.target.value)} inputMode="tel" required={required} />
        </div>
      ) : (
        <input
          className="input w-full"
          type={field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
        />
      )}
      {field.prefill && (
        <div className="text-[10px] text-slate-400 mt-1">prefill from contact.<code>{field.prefill}</code></div>
      )}
    </div>
  );
}

function SourcePill({ source }) {
  const s = (source || '').toLowerCase();
  if (s === 'wa')    return <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">WA</span>;
  if (s === 'email') return <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">Email</span>;
  return <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Direct</span>;
}

function FormSettings({ form, onSave }) {
  const [name, setName]         = useState(form.name);
  const [description, setDescription] = useState(form.description || '');
  return (
    <div className="card p-5 space-y-3 max-w-xl">
      <div>
        <div className="label-l">Form name</div>
        <input className="input w-full" value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div>
        <div className="label-l">Description (admin-only)</div>
        <textarea className="input w-full h-20" value={description} onChange={e => setDescription(e.target.value)} />
      </div>
      <div className="flex justify-end">
        <button className="btn" onClick={() => onSave({ name, description })}>Save</button>
      </div>
    </div>
  );
}
